import {
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { NzTooltipModule } from "ng-zorro-antd/tooltip";
import { NzPopoverModule } from "ng-zorro-antd/popover";
import { IconComponent } from "../icon/icon.component";
import { ImagePreviewBadge } from "./image-preview.types";
import { StoryboardService } from "src/app/services/storyboard.service";

interface StoryboardImageEditLlmEntry {
  recipe?: string;
  option?: string;
  notes?: string;
  ref_id?: string;
  target?: {
    label?: string;
    source?: string;
    location?: string | null;
    spec_ref?: string;
  };
}

export interface ImagePreviewThumbnail {
  id: number | string;
  url: string;
  label?: string;
  status?: string;
}

@Component({
  selector: "app-image-preview-overlay",
  standalone: true,
  imports: [CommonModule, NzTooltipModule, NzPopoverModule, IconComponent],
  templateUrl: "./image-preview-overlay.component.html",
  styleUrl: "./image-preview-overlay.component.scss",
})
export class ImagePreviewOverlayComponent implements OnChanges, OnDestroy {
  private readonly storyboardService = inject(StoryboardService);

  @Input() open = false;
  @Input() url = "";
  /** Original / parent image URL for edited variations (`edit_from_url`). */
  @Input() compareUrl = "";
  @Input() alt = "";
  @Input() title = "";
  /** Source variation identifier, shown alongside the preview title when available. */
  @Input() variationId: number | string | null | undefined = null;
  @Input() subtitle?: string;
  @Input() badges: ImagePreviewBadge[] = [];
  /** Full generation prompt shown from the control beneath the zoom rail. */
  @Input() prompt = "";
  /** When set with `editLlmShotId`, edited badge shows an info popover with LLM edit data. */
  @Input() editLlmVariationId: number | null = null;
  @Input() editLlmShotId: number | null = null;
  @Input() mediaType: "image" | "video" = "image";
  @Input() showNavigation = false;
  @Input() canNavigatePrev = false;
  @Input() canNavigateNext = false;
  @Input() prevLabel = "Previous variation";
  @Input() nextLabel = "Next variation";
  /** Optional variation filmstrip displayed below the active preview. */
  @Input() thumbnails: ImagePreviewThumbnail[] = [];
  @Input() activeThumbnailIndex = -1;
  @Input() showMarkReadyForReview = false;
  @Input() showRemoveFromReadyForReview = false;
  @Input() showApprove = false;
  @Input() showRevokeApproval = false;
  @Input() markReadyForReviewDisabled = false;
  @Input() removeFromReadyForReviewDisabled = false;
  @Input() approveDisabled = false;
  @Input() revokeApprovalDisabled = false;
  @Input() approveTooltip = "Approve";
  @Input() showEdit = false;
  @Input() showGenerate = false;
  @Input() showUpscale = false;
  @Input() showDownload = false;
  @Input() editDisabled = false;
  @Input() generateDisabled = false;
  @Input() upscaleDisabled = false;
  @Input() downloadDisabled = false;
  @Input() editTooltip = "Edit this image with AI";
  @Input() generateTooltip = "Generate more variations";
  @Input() upscaleTooltip = "Upscale this video";
  @Input() downloadTooltip = "Download";
  /** Optional filename for the saved file; falls back to the URL's basename. */
  @Input() downloadFilename = "";
  /** Icon-only badges and toolbar labels for tall portrait ratios (9:16, 4:5). */
  @Input() portraitCompact = false;

  @Output() closed = new EventEmitter<void>();
  @Output() navigatePrev = new EventEmitter<void>();
  @Output() navigateNext = new EventEmitter<void>();
  @Output() thumbnailSelect = new EventEmitter<number>();
  @Output() markReadyForReview = new EventEmitter<void>();
  @Output() removeFromReadyForReview = new EventEmitter<void>();
  @Output() approve = new EventEmitter<void>();
  @Output() revokeApproval = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();
  @Output() generate = new EventEmitter<void>();
  @Output() upscale = new EventEmitter<void>();
  @Output() download = new EventEmitter<void>();

  showOriginalImage = false;
  zoom = 1;
  panX = 0;
  panY = 0;
  private isPanning = false;
  private activePointerId: number | null = null;
  private pointerStartX = 0;
  private pointerStartY = 0;
  private panStartX = 0;
  private panStartY = 0;
  editInfoPanelVisible = false;
  editLlmLoading = false;
  editLlmLoadError = "";
  editLlmEdits: StoryboardImageEditLlmEntry[] = [];
  private editLlmDataKey = "";
  private editInfoPanelCloseTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["url"] || changes["compareUrl"] || changes["open"]) {
      this.showOriginalImage = false;
      this.resetZoom();
    }
    if (
      changes["editLlmVariationId"] ||
      changes["editLlmShotId"] ||
      changes["open"]
    ) {
      this.resetEditLlmState();
      this.clearEditInfoPanelCloseTimer();
      this.editInfoPanelVisible = false;
    }
  }

  ngOnDestroy(): void {
    this.clearEditInfoPanelCloseTimer();
  }

  private resetEditLlmState(): void {
    this.editLlmLoading = false;
    this.editLlmLoadError = "";
    this.editLlmEdits = [];
    this.editLlmDataKey = "";
  }

  private clearEditInfoPanelCloseTimer(): void {
    if (this.editInfoPanelCloseTimer != null) {
      clearTimeout(this.editInfoPanelCloseTimer);
      this.editInfoPanelCloseTimer = null;
    }
  }

  get showCompareToggle(): boolean {
    return (
      !!String(this.compareUrl ?? "").trim() && !!String(this.url ?? "").trim()
    );
  }

  get displayUrl(): string {
    if (this.showOriginalImage && this.showCompareToggle) {
      return this.compareUrl;
    }
    return this.url;
  }

  /** Filled portion of the zoom track, across the 1x to 3x range. */
  get zoomFill(): string {
    return `${Math.round(((this.zoom - 1) / 2) * 100)}%`;
  }

  stepZoom(delta: number): void {
    const next = this.clamp(Number((this.zoom + delta).toFixed(2)), 1, 3);
    if (next === this.zoom) return;

    this.zoom = next;
    if (this.zoom === 1) {
      this.panX = 0;
      this.panY = 0;
    }
  }

  get imageTransform(): string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
  }

  onZoomChange(event: Event): void {
    this.zoom = Number((event.target as HTMLInputElement).value);
    this.panX = 0;
    this.panY = 0;
  }

  onImageWheel(event: WheelEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const zoomDelta = event.deltaY < 0 ? 0.1 : -0.1;
    const nextZoom = this.clamp(Number((this.zoom + zoomDelta).toFixed(1)), 1, 3);
    if (nextZoom === this.zoom) return;

    this.zoom = nextZoom;
    if (this.zoom === 1) {
      this.panX = 0;
      this.panY = 0;
    }
  }

  onImagePointerDown(event: PointerEvent, image: HTMLImageElement): void {
    if (this.zoom <= 1 || event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    this.isPanning = true;
    this.activePointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.panStartX = this.panX;
    this.panStartY = this.panY;
    image.setPointerCapture(event.pointerId);
  }

  onImagePointerMove(event: PointerEvent, image: HTMLImageElement): void {
    if (!this.isPanning || this.activePointerId !== event.pointerId) return;
    event.preventDefault();
    const maxPanX = (image.clientWidth * (this.zoom - 1)) / 2;
    const maxPanY = (image.clientHeight * (this.zoom - 1)) / 2;
    this.panX = this.clamp(this.panStartX + event.clientX - this.pointerStartX, -maxPanX, maxPanX);
    this.panY = this.clamp(this.panStartY + event.clientY - this.pointerStartY, -maxPanY, maxPanY);
  }

  onImagePointerUp(event: PointerEvent): void {
    if (this.activePointerId !== event.pointerId) return;
    this.isPanning = false;
    this.activePointerId = null;
  }

  private resetZoom(): void {
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.isPanning = false;
    this.activePointerId = null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  setCompareView(showOriginal: boolean, event?: Event): void {
    event?.stopPropagation();
    if (!this.showCompareToggle) return;
    this.showOriginalImage = showOriginal;
    this.resetZoom();
  }

  get displayTitle(): string {
    const title = (this.title || this.alt).trim();
    const variationId = String(this.variationId ?? "").trim();
    return variationId ? `${title} [${variationId}]` : title;
  }

  /** Upscale is video-only; parents still opt in via `showUpscale`. */
  get showUpscaleButton(): boolean {
    return this.showUpscale && this.mediaType === "video";
  }

  get showToolbarActions(): boolean {
    return (
      this.showEdit ||
      this.showGenerate ||
      this.showUpscaleButton ||
      this.showDownload
    );
  }

  get showApprovalActions(): boolean {
    return (
      this.showMarkReadyForReview ||
      this.showRemoveFromReadyForReview ||
      this.showApprove ||
      this.showRevokeApproval
    );
  }

  onEditClick(event: Event): void {
    event.stopPropagation();
    if (!this.editDisabled) {
      this.edit.emit();
    }
  }

  onGenerateClick(event: Event): void {
    event.stopPropagation();
    if (!this.generateDisabled) {
      this.generate.emit();
    }
  }

  onUpscaleClick(event: Event): void {
    event.stopPropagation();
    if (!this.upscaleDisabled) {
      this.upscale.emit();
    }
  }

  onDownloadClick(event: Event): void {
    event.stopPropagation();
    if (this.downloadDisabled) {
      return;
    }
    void this.triggerDownload(this.displayUrl, this.resolveDownloadFilename());
  }

  /** Derive a sensible filename from the explicit input or the URL basename. */
  private resolveDownloadFilename(): string {
    const explicit = String(this.downloadFilename ?? "").trim();
    if (explicit) {
      return explicit;
    }
    const url = String(this.displayUrl ?? "").trim();
    try {
      const path = new URL(url, document.baseURI).pathname;
      const base = decodeURIComponent(path.split("/").pop() ?? "");
      if (base) {
        return base;
      }
    } catch {
      // Malformed URL — fall through to the generic name.
    }
    return this.mediaType === "video" ? "download.mp4" : "download";
  }

  /** Force a real file-save by fetching the URL as a Blob (same-origin blob
   *  URLs honor the `download` attribute even when the upstream serves the
   *  file inline). Falls back to a new-tab download if the fetch fails
   *  (CORS / network). */
  private async triggerDownload(url: string, filename: string): Promise<void> {
    const target = String(url ?? "").trim();
    if (!target) {
      return;
    }
    try {
      const resp = await fetch(target, { mode: "cors", credentials: "omit" });
      if (!resp.ok) {
        throw new Error(`fetch failed: ${resp.status}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      this.clickDownloadAnchor(objectUrl, filename, false);
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      this.clickDownloadAnchor(target, filename, true);
    }
  }

  private clickDownloadAnchor(
    href: string,
    filename: string,
    newTab: boolean,
  ): void {
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    a.rel = "noopener noreferrer";
    if (newTab) {
      a.target = "_blank";
    }
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    setTimeout(() => a.remove(), 1000);
  }

  onOverlayClick(): void {
    this.closed.emit();
  }

  onCloseClick(event: Event): void {
    event.stopPropagation();
    this.closed.emit();
  }

  onPrevClick(event: Event): void {
    event.stopPropagation();
    if (this.canNavigatePrev) {
      this.navigatePrev.emit();
    }
  }

  onNextClick(event: Event): void {
    event.stopPropagation();
    if (this.canNavigateNext) {
      this.navigateNext.emit();
    }
  }

  onThumbnailClick(index: number, event: Event): void {
    event.stopPropagation();
    if (index === this.activeThumbnailIndex) {
      return;
    }
    this.thumbnailSelect.emit(index);
  }

  isStatusBadge(badge: ImagePreviewBadge): boolean {
    return (
      badge.variant === "status-ready" ||
      badge.variant === "status-approved" ||
      badge.variant === "status-feedback"
    );
  }

  badgeIcon(badge: ImagePreviewBadge): string | null {
    if (badge.icon) {
      return badge.icon;
    }
    if (badge.variant === "status-approved") {
      return "check-circle";
    }
    if (badge.variant === "status-ready") {
      return "clock";
    }
    if (badge.variant === "status-feedback") {
      return "info";
    }
    if (badge.showStatusIcon) {
      return "circle-plus";
    }
    if (badge.variant === "edited" || badge.variant === "variation-edited") {
      return "edit";
    }
    if (badge.variant === "variation-uploaded") {
      return "upload";
    }
    return null;
  }

  hideBadgeLabel(badge: ImagePreviewBadge): boolean {
    if (!this.portraitCompact) {
      return false;
    }
    return (
      badge.variant === "status-approved" ||
      badge.variant === "variation-uploaded"
    );
  }

  badgeTooltip(badge: ImagePreviewBadge): string | null {
    if (badge.tooltip) {
      return badge.tooltip;
    }
    if (!this.hideBadgeLabel(badge)) {
      return null;
    }
    return badge.label;
  }

  badgeClass(badge: ImagePreviewBadge): Record<string, boolean> {
    const variant = badge.variant ?? "default";
    return {
      "image-preview-badge": true,
      [`image-preview-badge--${variant}`]: true,
      "image-preview-badge--status": this.isStatusBadge(badge),
      "image-preview-badge--with-info": this.isEditedBadgeWithInfo(badge),
    };
  }

  isEditedBadgeWithInfo(badge: ImagePreviewBadge): boolean {
    const isEdited =
      badge.variant === "edited" || badge.variant === "variation-edited";
    return (
      isEdited &&
      this.editLlmVariationId != null &&
      this.editLlmShotId != null
    );
  }

  openEditInfoPanel(): void {
    this.clearEditInfoPanelCloseTimer();
    this.editInfoPanelVisible = true;
    this.loadEditLlmData();
  }

  onEditInfoPanelMouseLeave(): void {
    this.editInfoPanelCloseTimer = setTimeout(() => {
      this.editInfoPanelVisible = false;
    }, 160);
  }

  onEditInfoPanelMouseEnter(): void {
    this.clearEditInfoPanelCloseTimer();
  }

  private loadEditLlmData(): void {
    const variationId = Number(this.editLlmVariationId);
    const shotId = Number(this.editLlmShotId);
    if (!Number.isFinite(variationId) || !Number.isFinite(shotId)) {
      return;
    }

    const cacheKey = `${shotId}:${variationId}`;
    if (
      this.editLlmDataKey === cacheKey &&
      (this.editLlmEdits.length || this.editLlmLoadError)
    ) {
      return;
    }
    if (this.editLlmDataKey === cacheKey && this.editLlmLoading) {
      return;
    }

    this.editLlmDataKey = cacheKey;
    this.editLlmLoading = true;
    this.editLlmLoadError = "";
    this.editLlmEdits = [];

    this.storyboardService
      .getStoryboardImageLlmData({
        shot_id: shotId,
        variation_id: variationId,
      })
      .subscribe({
        next: (res: any) => {
          if (this.editLlmDataKey !== cacheKey) return;
          this.editLlmLoading = false;
          if (res?.statusCode !== 200) {
            this.editLlmLoadError =
              res?.message ?? "Failed to load edit details";
            return;
          }
          const edits =
            res?.data?.llm_data?.input_data?.generation_job_input_data?.edits ??
            [];
          this.editLlmEdits = Array.isArray(edits) ? edits : [];
        },
        error: (err: any) => {
          if (this.editLlmDataKey !== cacheKey) return;
          this.editLlmLoading = false;
          this.editLlmLoadError =
            err?.error?.message ?? "Failed to load edit details";
        },
      });
  }

  getEditLlmItemTitle(edit: StoryboardImageEditLlmEntry): string {
    const recipe = String(edit?.recipe ?? "").trim();
    const option = String(edit?.option ?? "").trim();
    if (recipe && option) return `${recipe} · ${option}`;
    return option || recipe || "Edit";
  }

  getEditLlmItemBody(edit: StoryboardImageEditLlmEntry): string {
    const parts: string[] = [];
    const notes = String(edit?.notes ?? "").trim();
    const targetLabel = String(edit?.target?.label ?? "").trim();
    const targetSource = String(edit?.target?.source ?? "").trim();
    if (targetLabel) {
      parts.push(
        targetSource
          ? `Target: ${targetLabel} (${targetSource})`
          : `Target: ${targetLabel}`,
      );
    }
    if (notes) {
      parts.push(notes);
    }
    return parts.join("\n\n") || "No details for this edit.";
  }

  @HostListener("document:keydown", ["$event"])
  onKeydown(event: KeyboardEvent): void {
    if (!this.open) {
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      this.closed.emit();
      return;
    }
    if (event.key === "ArrowLeft" && this.canNavigatePrev) {
      event.preventDefault();
      this.navigatePrev.emit();
      return;
    }
    if (event.key === "ArrowRight" && this.canNavigateNext) {
      event.preventDefault();
      this.navigateNext.emit();
    }
  }
}
